import React, { createContext, useContext, useState, useCallback } from 'react';

const AuthContext = createContext(null);

const STORAGE_KEY = 'ilakku_user';
const TOKEN_KEY   = 'ilakku_token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Token kept in its own key so api.js can read it without parsing user object
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);

  const login = useCallback((userData) => {
    const { token: newToken, ...rest } = userData;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
    }
    setUser(rest);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);
  }, []);

  const updateUser = useCallback((updates) => {
    // If a fresh token came back (e.g. after change-password), store it
    const { token: newToken, ...rest } = updates;
    const updated = { ...user, ...rest };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      setToken(newToken);
    }
    setUser(updated);
  }, [user]);

  // Role helpers
  const isAdmin     = user?.role === 'Admin';
  const isPM        = user?.role === 'Project Manager';
  const isDev       = user?.role === 'Developer';
  const isAdminOrPM = isAdmin || isPM;

  // Permission checks
  const can = (action) => {
    if (!user) return false;
    switch (action) {
      // Admin only
      case 'manageUsers':       return isAdmin;
      case 'resetAnyPassword':  return isAdmin;
      // Admin + PM
      case 'viewDashboard':     return isAdminOrPM;
      case 'manageEmployees':   return isAdminOrPM;
      case 'viewResources':     return isAdminOrPM;
      case 'viewTaskReport':    return isAdminOrPM;
      case 'manageChanges':     return isAdminOrPM;
      case 'manageRAID':        return isAdminOrPM;
      case 'createEditProject': return isAdminOrPM;
      case 'createEditTask':    return true;  // All roles can edit tasks
      case 'createTaskInAlloc': return isAdminOrPM;
      // All roles
      case 'viewProjects':      return true;
      case 'addNotes':          return true;
      case 'viewTasks':         return true;
      case 'viewAllocation':    return true;
      case 'changeOwnPassword': return true;
      default:                  return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, can, isAdmin, isPM, isDev, isAdminOrPM }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export default AuthContext;
